import { Observable, combineLatest, map } from 'rxjs';
import * as R from 'ramda';
import { LiquidityAmount, Token } from '@akropolis-web/primitives';

import { protocolsApi, ratingsApi } from 'api/modules';
import {
  ImpactCosts,
  MarketBadDebtRatingInfo,
  MarketManipulationComplexityRatingInfo,
  NetworkSlug,
  ProductSlug,
  Rating,
  VersionSlug,
} from 'domain/types';
import { Either, left, right } from 'utils/either';
import { isEqualContracts } from 'utils/validators';
import { compareAmounts } from 'utils/amount';
import { ERROR_MESSAGES } from 'domain/utils';

import { getMarketBadDebtRatingInfo, getMarketManipulationComplexityRatingInfo } from '../ratings';

export type CostsTableData = {
  token: Token;
  pumpCost: Either<LiquidityAmount>;
  pumpCostModel: Either<string>;
  dumpCost: Either<LiquidityAmount>;
  dumpCostModel: Either<string>;
};

export type MarketRatingsPageData = {
  rating: Either<Rating>;
  manipulationComplexityInfo: MarketManipulationComplexityRatingInfo | null;
  badDebtInfo: MarketBadDebtRatingInfo | null;
  costsTableData: CostsTableData[];
};

export function getMarketRatingsPageData({
  productSlug,
  versionSlug,
  network,
  marketId,
}: {
  productSlug: ProductSlug;
  versionSlug: VersionSlug;
  network: NetworkSlug;
  marketId: string;
}): Observable<MarketRatingsPageData> {
  return combineLatest([
    getMarketManipulationComplexityRatingInfo({
      marketId,
      network,
      productSlug,
      versionSlug,
    }),
    getMarketBadDebtRatingInfo({ productSlug, versionSlug, network, marketId }),
    ratingsApi.getWorstMarketRating$({ productSlug, versionSlug, network, marketId }),
    protocolsApi.getImpactCosts$({ productSlug, versionSlug, network }, marketId),
  ]).pipe(
    map(
      ([
        manipulationComplexityInfo,
        badDebtInfo,
        marketRating,
        impactCosts,
      ]): MarketRatingsPageData => {
        const costsTableData = R.uniqBy(cost => cost.token.address, impactCosts.all).map(
          ({ token }) => {
            const tokenImpacts = impactCosts.all.filter(x => isEqualContracts(token, x.token));
            const pumpImpact: ImpactCosts | undefined = [...tokenImpacts]
              .sort((a, b) => compareAmounts(a.pumpCost, b.pumpCost))
              .reverse()[0];
            const dumpImpact: ImpactCosts | undefined = [...tokenImpacts]
              .sort((a, b) => compareAmounts(a.dumpCost, b.dumpCost))
              .reverse()[0];

            return {
              token,
              pumpCost: pumpImpact
                ? right(pumpImpact.pumpCost)
                : left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA),
              pumpCostModel: pumpImpact
                ? right(pumpImpact.model)
                : left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA),
              dumpCost: dumpImpact
                ? right(dumpImpact.dumpCost)
                : left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA),
              dumpCostModel: dumpImpact
                ? right(dumpImpact.model)
                : left(ERROR_MESSAGES.UNKNOWN_IMPACT_DATA),
            };
          },
        );
        return {
          rating: marketRating.map(r => r.rating),
          badDebtInfo,
          manipulationComplexityInfo,
          costsTableData,
        };
      },
    ),
  );
}
